'use client';

// Third Party Plugins
import React, { useState, useCallback, useEffect } from 'react';
// Types
import { ClientSideResourcesWrapperProps, Resource } from '../../types';
// Custom Components
import SearchForm from '../SearchForm';
import ResourceList from '../ResourceList';
import ImageContainer from '@/components/images/ImageContainer';
import LayoutContainer from '@/components/ui/LayoutContainer';
import ContentBlock from '@/components/blocks/ContentBlock';
import Logo from '@/components/images/Logo';
import HeroHeading from '@/components/ui/HeroHeading';
import Gallery from '@/components/images/Gallery';

const ClientSideResourcesWrapper: React.FC<ClientSideResourcesWrapperProps> = ({
  headingOne,
  subheading,
  pageImageBlock,
  resourcesManyBlock,
  hasGallery,
  galleryManyBlock,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [resources, setResources] = useState<Resource[]>(resourcesManyBlock);

  const searchResources = useCallback(() => {
    if (!searchTerm.trim()) {
      setResources(resourcesManyBlock);
      return;
    }
    const items = resourcesManyBlock.filter((resource) =>
      resource.resourceBlockHeading
        .trim()
        .toLowerCase()
        .includes(searchTerm.trim().toLowerCase()),
    );
    setResources(items || []);
  }, [searchTerm, resourcesManyBlock]);

  useEffect(() => {
    searchResources();
  }, [searchTerm, searchResources]);

  return (
    <>
      <HeroHeading>
        <Logo />
        <h1>{headingOne}</h1>
        <p>{subheading}</p>
        {pageImageBlock ? (
          <ImageContainer
            image={pageImageBlock.asset.url}
            width={751}
            height={194}
            type="dynamic"
            alt={pageImageBlock.alt || ''}
          />
        ) : null}
        <SearchForm searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
      </HeroHeading>
      <LayoutContainer hasSectionGaps>
        <ResourceList resources={resources} />
        {hasGallery ? (
          <Gallery
            images={galleryManyBlock.map((image) => ({
              alt: image.alt || '',
              url: image.asset?.url || '', // Move the URL to the top level
              asset: image.asset,
              crop: image.crop,
              hotspot: image.hotspot,
            }))}
          />
        ) : null}
        <ContentBlock
          showCallToAction={true}
          callToActionLink="/contact?source=add-resource"
          callToActionType="internal"
          callToActionTitle="Suggest a Resource"
        >
          <div style={{ fontWeight: `bold`, fontSize: `18px` }}>
            If you have a resource you would like to suggest please click on the
            button below:
          </div>
        </ContentBlock>
      </LayoutContainer>
    </>
  );
};

export default ClientSideResourcesWrapper;
